import type {
  AcceptEarlyStartResult,
  AdminOnboardingBrandRow,
  ApprovalHistoryRow,
  BrandOnboardingRecord,
  ConfirmOnboardingFieldsInput,
  CreateOnboardingBrandInput,
  CreateOnboardingBrandResult,
  DecideOnboardingApprovalInput,
  MarkOnboardingBrandLiveInput,
  OnboardingApprovalView,
  FakeSignInput,
  RecordSignedOutsideInput,
  RecordSignedOutsideResult,
  GetBrandOnboardingInput,
  SendDocumentsResult,
  SignatureRefresh,
  SigningSetup,
  SigningUrlResult,
  UpdateOnboardingSectionInput,
  UploadOnboardingPhotoInput,
} from '@globalbridge/types';
import type { ReactNode } from 'react';
import { trpc } from '../../lib/trpc';

/**
 * brandOnboarding.* hooks (Vera onboarding spec 2026-10-12, P1). Brand users
 * omit brandId (the server uses their own brand); admins pass the brand's
 * Snowflake id. Every reference to the router lives in this file, so the
 * contract (packages/types/src/brand-onboarding.ts) is consumed in one place.
 *
 * The return types are written out from the Zod contract on purpose: callers
 * stay typed against the contract, not against whatever the router infers.
 */

interface Query<T> {
  data: T | undefined;
  isLoading: boolean;
  error: { message: string } | null;
  refetch: () => unknown;
}

interface MutateOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (e: { message: string }) => void;
}

interface Mutation<I, O> {
  mutate: (input: I, opts?: MutateOptions<O>) => void;
  mutateAsync: (input: I) => Promise<O>;
  /** Clears the last result and error (a dialog that closes should not leak its error into the next). */
  reset: () => void;
  isPending: boolean;
  error: { message: string } | null;
}

export function useBrandOnboarding(brandId?: number, opts: { enabled?: boolean } = {}): Query<BrandOnboardingRecord> {
  return trpc.brandOnboarding.get.useQuery({ brandId }, { staleTime: 30_000, enabled: opts.enabled ?? true });
}

export function useUpdateOnboardingSection(): Mutation<UpdateOnboardingSectionInput, BrandOnboardingRecord> {
  const utils = trpc.useUtils();
  return trpc.brandOnboarding.updateSection.useMutation({ onSuccess: () => utils.brandOnboarding.get.invalidate() });
}

/** The brand confirms named items (Ari, 2026-10-13: per field). The API refuses the team. */
export function useConfirmOnboardingFields(): Mutation<ConfirmOnboardingFieldsInput, BrandOnboardingRecord> {
  const utils = trpc.useUtils();
  return trpc.brandOnboarding.confirmFields.useMutation({ onSuccess: () => utils.brandOnboarding.get.invalidate() });
}

export function useUploadOnboardingPhoto(): Mutation<UploadOnboardingPhotoInput, { photoUrl: string }> {
  return trpc.brandOnboarding.uploadProductPhoto.useMutation();
}

export function useOnboardingBrandsAdmin(): Query<AdminOnboardingBrandRow[]> {
  return trpc.brandOnboarding.adminList.useQuery(undefined, { staleTime: 30_000 });
}

export function useCreateOnboardingBrand(): Mutation<CreateOnboardingBrandInput, CreateOnboardingBrandResult> {
  const utils = trpc.useUtils();
  return trpc.brandOnboarding.adminCreateBrand.useMutation({ onSuccess: () => utils.brandOnboarding.adminList.invalidate() });
}

export function useMarkOnboardingBrandLive(): Mutation<MarkOnboardingBrandLiveInput, { brandDbId: number; liveAt: string }> {
  const utils = trpc.useUtils();
  return trpc.brandOnboarding.markLive.useMutation({ onSuccess: () => utils.brandOnboarding.adminList.invalidate() });
}

/** The brand starts product onboarding once documents are signed (spec section 8). The API refuses the team. */
export function useAcceptEarlyStart(): Mutation<GetBrandOnboardingInput, AcceptEarlyStartResult> {
  const utils = trpc.useUtils();
  return trpc.brandOnboarding.acceptEarlyStart.useMutation({
    onSuccess: () => Promise.all([utils.brandOnboarding.get.invalidate(), utils.users.me.invalidate()]),
  });
}

export function useOnboardingApproval(brandId?: number): Query<OnboardingApprovalView> {
  return trpc.brandOnboarding.approval.useQuery({ brandId }, { staleTime: 30_000 });
}

/** The brand sends its first assortment for review. The API refuses the team. */
export function useSubmitAssortment(): Mutation<{ brandId?: number }, { id: string; submittedAt: string; email: 'sent' | 'failed' | 'skipped' }> {
  const utils = trpc.useUtils();
  return trpc.brandOnboarding.submitAssortment.useMutation({
    onSuccess: () => {
      void utils.brandOnboarding.approval.invalidate();
      void utils.brandOnboarding.get.invalidate();
    },
  });
}

/** Whether documents can be sent at all (SIGNING_PROVIDER set on the API). */
export function useSigningSetup(brandId?: number): Query<SigningSetup> {
  return trpc.brandOnboarding.signingSetup.useQuery({ brandId }, { staleTime: 60_000 });
}

/** The team sends the documents. The API refuses the brand. */
export function useSendDocuments(): Mutation<{ brandId?: number }, SendDocumentsResult> {
  const utils = trpc.useUtils();
  return trpc.brandOnboarding.sendDocuments.useMutation({
    onSuccess: () => {
      void utils.brandOnboarding.get.invalidate();
      void utils.brandOnboarding.adminList.invalidate();
    },
  });
}

/** "Mark documents signed": the team records documents signed outside the portal. The API refuses the brand. */
export function useRecordSignedOutside(): Mutation<RecordSignedOutsideInput, RecordSignedOutsideResult> {
  const utils = trpc.useUtils();
  return trpc.brandOnboarding.recordSignedOutside.useMutation({
    onSuccess: () => {
      void utils.brandOnboarding.get.invalidate();
      void utils.brandOnboarding.adminList.invalidate();
    },
  });
}

/** "Review and sign": the brand gets the provider's signing page address. */
export function useOpenSigning(): Mutation<{ brandId?: number }, SigningUrlResult> {
  return trpc.brandOnboarding.signingUrl.useMutation();
}

/** Reads the provider's status into the record. */
export function useRefreshSignature(): Mutation<{ brandId?: number }, SignatureRefresh> {
  const utils = trpc.useUtils();
  return trpc.brandOnboarding.refreshSignature.useMutation({ onSuccess: () => utils.brandOnboarding.get.invalidate() });
}

/** Test signing page only (fake provider). */
export function useFakeSign(): Mutation<FakeSignInput, SignatureRefresh> {
  const utils = trpc.useUtils();
  return trpc.brandOnboarding.fakeSign.useMutation({ onSuccess: () => utils.brandOnboarding.get.invalidate() });
}

export function useDecideApproval(): Mutation<
  DecideOnboardingApprovalInput,
  { approvalStatus: 'pending' | 'approved' | 'rejected'; closed: boolean; email: 'sent' | 'failed' | 'skipped' }
> {
  const utils = trpc.useUtils();
  return trpc.brandOnboarding.decideApproval.useMutation({
    onSuccess: () => {
      void utils.brandOnboarding.approvalHistory.invalidate();
      void utils.admin.actionRequestsList.invalidate();
      void utils.admin.actionRequestsPendingCount.invalidate();
    },
  });
}

export function useApprovalHistory(actionRequestId: string): Query<ApprovalHistoryRow[]> {
  return trpc.brandOnboarding.approvalHistory.useQuery({ actionRequestId }, { staleTime: 10_000 });
}

/**
 * The writes that persist the record (a field or a section): only these count
 * as "Saved" in the slim bar. A photo upload is not a save: the product holds
 * the photo only once the brand saves the product. tRPC v11 mutation keys are
 * `[['brandOnboarding', <proc>]]`.
 */
export const RECORD_WRITE_MUTATION_KEYS = [
  [['brandOnboarding', 'updateSection']],
  [['brandOnboarding', 'confirmFields']],
] as const;

export function isRecordWriteKey(key: unknown): boolean {
  const k = JSON.stringify(key);
  return RECORD_WRITE_MUTATION_KEYS.some((w) => JSON.stringify(w) === k);
}

/**
 * The seam Vera's column reads (spec §5.3). P2 fills it from the onboarding
 * stream: pages/onboarding/vera/useOnboardingChat.tsx.
 */
export interface OnboardingChat {
  /** The thread, oldest first, already rendered by the chat module. */
  thread: ReactNode;
  busy: boolean;
  send: (text: string) => void;
  /** The team, or a role that cannot act, reads the brand's thread; only acting brand roles write to Vera (Ari, 2026-10-13; ruling C5). */
  readOnly: boolean;
}
